import { Injectable, inject } from '@angular/core';
import { ConsumerAudioPreferencesService } from './consumer-audio-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class SoundUnlockService {
  #audioPreferences = inject(ConsumerAudioPreferencesService);

  private audioContext: AudioContext | null = null;
  private unlocked = false;
  private unlockingPromise: Promise<boolean> | null = null;

  public get isUnlocked(): boolean {
    return this.unlocked;
  }

  public markUnlocked(): void {
    if (!this.unlocked) {
      this.unlocked = true;
    }
    this.#audioPreferences.unlockSound();
  }

  public async unlock(): Promise<boolean> {
    if (this.unlocked) {
      this.markUnlocked();
      return true;
    }

    if (this.unlockingPromise) {
      return this.unlockingPromise;
    }

    const run = async (): Promise<boolean> => {
      if (typeof window === 'undefined') {
        this.unlocked = true;
        this.#audioPreferences.unlockSound();
        return true;
      }

      const Ctx = (window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctx) {
        this.unlocked = true;
        this.#audioPreferences.unlockSound();
        return true;
      }

      try {
        if (!this.audioContext) {
          this.audioContext = new Ctx({
            latencyHint: 'interactive'
          });
        }

        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        if (this.audioContext.state === 'running') {
          this.markUnlocked();
          return true;
        }
      } catch (error) {
        console.warn('Failed to unlock audio context', error);
      }

      return false;
    };

    try {
      this.unlockingPromise = run();
      const result = await this.unlockingPromise;
      return result;
    } finally {
      this.unlockingPromise = null;
    }
  }
}
